import { useEffect, useState } from "react";
import { listAssets } from "../lib/generate/listAssets.js";
import { listFolders } from "../lib/folders/folders.js";
import { deleteAsset } from "../lib/generate/deleteAsset.js";
import { canEdit } from "../constants.js";
import AssetsTable from "../Assets/AssetsTable.js";
import FolderRail from "../Assets/FolderRail.js";
import ConfirmDialog from "../Components/ConfirmDialog.js";
import "./AssetsTab.css";

export default function AssetsTab({ appConfig, brand }) {
  const editable = canEdit(appConfig.userRole);
  const [assets, setAssets] = useState([]);
  const [folders, setFolders] = useState([]);
  const [activeFolder, setActiveFolder] = useState("all");
  const [isLoading, setIsLoading] = useState(true);
  const [apiResponse, setApiResponse] = useState(null);
  const [error, setError] = useState(null);
  const [actionError, setActionError] = useState(null);
  const [deletingAssetId, setDeletingAssetId] = useState(null);
  const [confirmingDelete, setConfirmingDelete] = useState(null); // the asset pending a confirm/cancel choice

  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);
    setError(null);

    (async () => {
      try {
        const [assetList, folderList] = await Promise.all([
          listAssets(appConfig, brand.id, () => {}, setApiResponse),
          listFolders(appConfig, brand.id, () => {}, () => {}),
        ]);
        if (!cancelled) {
          setAssets(assetList);
          setFolders(folderList);
        }
      } catch (err) {
        if (!cancelled) setError("Couldn't load assets. Please try again.");
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [brand.id]);

  async function handleDeleteAsset() {
    if (!confirmingDelete) return;
    const asset = confirmingDelete;
    setActionError(null);
    setDeletingAssetId(asset.id);
    try {
      await deleteAsset(appConfig, { brandId: brand.id, assetId: asset.id }, () => {}, () => {});
      setAssets((current) => current.filter((a) => a.id !== asset.id));
      setConfirmingDelete(null);
    } catch (err) {
      setActionError(err.message);
    } finally {
      setDeletingAssetId(null);
    }
  }

  if (isLoading) {
    return <p className="assets-tab__status">Loading assets…</p>;
  }

  if (error) {
    return <p className="assets-tab__status assets-tab__status--error">{error}</p>;
  }

  if (assets.length === 0) {
    return (
      <>
        {actionError ? <p className="assets-tab__status assets-tab__status--error">{actionError}</p> : null}
        <p className="assets-tab__status">
          No assets yet for this brand — create one from the Generate wizard.
        </p>
      </>
    );
  }

  // No onMoveToFolder passed - folder tags are read-only here. Folder
  // management (create/rename/delete/move) lives on the global Assets page,
  // so FolderRail renders in readOnly mode here (navigation/filtering only).
  // Delete is available here too, since it isn't tied to folder management.
  return (
    <>
      {actionError ? <p className="assets-tab__status assets-tab__status--error">{actionError}</p> : null}
      <FolderRail
        folders={folders}
        assets={assets}
        activeFolder={activeFolder}
        onSelectFolder={setActiveFolder}
        readOnly
      />
      <AssetsTable
        assets={assets}
        brandSlug={brand.slug}
        folders={folders}
        activeFolder={activeFolder}
        onDelete={setConfirmingDelete}
        deletingId={deletingAssetId}
        editable={editable}
      />
      {confirmingDelete ? (
        <ConfirmDialog
          title={`Delete ${confirmingDelete.publicAssetCode ?? confirmingDelete.id}?`}
          message="This can't be undone."
          confirmLabel="Delete"
          danger
          isBusy={deletingAssetId === confirmingDelete.id}
          onConfirm={handleDeleteAsset}
          onCancel={() => setConfirmingDelete(null)}
        />
      ) : null}
    </>
  );
}